import { Navigate, Outlet, Route, useLocation } from 'react-router-dom';
import { useRhinoContext } from '@rhino-project/core';
import { AppOwnerProvider } from '@/contexts/AppOwnerProvider';
import Layout from '@/components/Layout';
import Landing from '@/pages/Landing';
import Register from '@/pages/Register';
import Opportunities from '@/pages/Opportunities';
import Resources from '@/pages/Resources';
import ResourcesFaq from '@/pages/ResourcesFaq';
import ResourcesGuides from '@/pages/ResourcesGuides';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import DefencePrimePathway from '@/pages/DefencePrimePathway';
import DndShowcase from '@/pages/DndShowcase';
import SponsorView from '@/pages/SponsorView';
import MySubmissions from '@/pages/MySubmissions';
import DualUseOnRamp from '@/pages/DualUseOnRamp';
import SignalCaptureTriage from '@/pages/SignalCaptureTriage';
import CapabilityTranslation from '@/pages/CapabilityTranslation';
import Login from '@/pages/Login';
import ResetPassword from '@/pages/ResetPassword';
import ReadinessRouting from '@/pages/ReadinessRouting';
import NavigatorDashboard from '@/pages/NavigatorDashboard';
import FileDetail from '@/pages/FileDetail';
import InboundQueue from '@/pages/InboundQueue';
import OpportunityDetail from '@/pages/OpportunityDetail';
import MyFiles from '@/pages/MyFiles';
import StrategicPartners from '@/pages/StrategicPartners';
import SubmissionDetail from '@/pages/SubmissionDetail';

const roleHomes = {
  dual_use_entrant: '/dual-use-on-ramp/my-submissions',
  capability_sponsor: '/sponsor-view',
  navigator: '/navigator-role',
  defence_prime: '/defence-prime-path',
  knowledge_manager: '/resources',
  system_administrator: '/dnd-showcase',
  technical_operations_lead: '/dnd-showcase'
};

const rolesFor = (rhino) => (rhino.user?.users_roles ?? rhino.usersRoles ?? [])
  .map((entry) => entry.role?.name)
  .filter(Boolean);

const firstHome = (roles) => roles.map((role) => roleHomes[role]).find(Boolean) ?? '/dual-use-on-ramp';

const AuthenticatedRoute = () => {
  const rhino = useRhinoContext();
  const location = useLocation();
  if (!rhino.user) {
    return <Navigate to="/login" replace state={{ redirectTo: location.pathname + location.search }} />;
  }
  return <Outlet />;
};

const RoleProtectedRoute = ({ roles, children }) => {
  const rhino = useRhinoContext();
  const userRoles = rolesFor(rhino);
  if (!rhino.user) return <Navigate to="/login" replace />;
  if (roles?.length && !roles.some((role) => userRoles.includes(role))) {
    return <Navigate to={firstHome(userRoles)} replace />;
  }
  return children;
};

export const customRoutes = () => [
  <Route key="layout" element={<AppOwnerProvider><Layout /></AppOwnerProvider>}>
    <Route path="/" element={<Landing />} />
    <Route path="/register" element={<Register />} />
    <Route path="/auth/signup" element={<Register />} />
    <Route path="/login" element={<Login />} />
    <Route path="/auth/signin" element={<Login />} />
    <Route path="/auth/reset-password" element={<ResetPassword />} />
    <Route path="/opportunities" element={<Opportunities />} />
    <Route path="/opportunities/:id" element={<OpportunityDetail />} />
    <Route path="/resources" element={<Resources />} />
    <Route path="/resources/faq" element={<ResourcesFaq />} />
    <Route path="/resources/guides" element={<ResourcesGuides />} />
    <Route path="/about" element={<About />} />
    <Route path="/contact" element={<Contact />} />
    <Route path="/dual-use-on-ramp" element={<DualUseOnRamp />} />
    <Route path="/defence-prime-path" element={<DefencePrimePathway />} />
    <Route path="/strategic-partners" element={<StrategicPartners />} />
    <Route path="/opportunities/:id/apply" element={<OpportunityDetail />} />
    <Route path="/company-profile" element={<DualUseOnRamp />} />
    <Route path="/clearance-status" element={<ReadinessRouting />} />
    <Route path="/admin/knowledge-base" element={<Resources />} />
    <Route path="/admin/taxonomy" element={<Resources />} />
    <Route path="/admin/users" element={<Resources />} />
    <Route path="/admin/analytics" element={<DndShowcase />} />
    <Route path="/admin/monitoring" element={<DndShowcase />} />

    <Route element={<AuthenticatedRoute />}>
      <Route path="/dnd-showcase" element={<RoleProtectedRoute roles={['capability_sponsor', 'system_administrator', 'technical_operations_lead', 'knowledge_manager', 'defence_prime']}><DndShowcase /></RoleProtectedRoute>} />
      <Route path="/sponsor-view" element={<RoleProtectedRoute roles={['capability_sponsor']}><SponsorView /></RoleProtectedRoute>} />
      <Route path="/sponsor-view/inbound-queue" element={<RoleProtectedRoute roles={['capability_sponsor']}><InboundQueue /></RoleProtectedRoute>} />
      <Route path="/sponsor-view/submission-detail" element={<RoleProtectedRoute roles={['capability_sponsor']}><SubmissionDetail /></RoleProtectedRoute>} />
      <Route path="/dual-use-on-ramp/my-submissions" element={<RoleProtectedRoute roles={['dual_use_entrant']}><MySubmissions /></RoleProtectedRoute>} />
      <Route path="/dual-use-on-ramp/intake" element={<RoleProtectedRoute roles={['dual_use_entrant']}><SignalCaptureTriage /></RoleProtectedRoute>} />
      <Route path="/dual-use-on-ramp/translation" element={<RoleProtectedRoute roles={['dual_use_entrant']}><CapabilityTranslation /></RoleProtectedRoute>} />
      <Route path="/dual-use-on-ramp/readiness-routing" element={<RoleProtectedRoute roles={['dual_use_entrant']}><ReadinessRouting /></RoleProtectedRoute>} />
      <Route path="/navigator-role" element={<RoleProtectedRoute roles={['navigator']}><NavigatorDashboard /></RoleProtectedRoute>} />
      <Route path="/navigator-role/file-detail" element={<RoleProtectedRoute roles={['navigator']}><FileDetail /></RoleProtectedRoute>} />
      <Route path="/navigator-role/my-files" element={<RoleProtectedRoute roles={['navigator']}><MyFiles /></RoleProtectedRoute>} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Route>
];
